import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { useIntl, isRtl, getLocale } from '@edx/frontend-platform/i18n';
import {
  ActionRow,
  Button,
  Form,
  StandardModal,
  useCheckboxSetValues,
} from '@openedx/paragon';

import messages from './messages';

export const FEEDBACK_REASON_BROKEN = 'broken';
export const FEEDBACK_REASON_HARMFUL = 'harmful';
export const FEEDBACK_REASON_NOT_ACCURATE = 'not_accurate';
export const FEEDBACK_REASON_OTHER = 'other';

const FeedbackDetailsModal = ({
  isOpen,
  close,
  currentFeedbackValue,
  onSendPositiveFeedback,
  onSendNegativeFeedback,
}) => {
  const { formatMessage } = useIntl();
  const siteLanguage = getLocale();
  const dir = isRtl(siteLanguage) ? 'rtl' : 'ltr';
  const [feedbackDetails, setFeedbackDetails] = useState('');
  const [emailMeCheckboxValue, setEmailMeCheckboxValue] = useState(true);

  const [reasonValues, { add, remove }] = useCheckboxSetValues([]);

  const onSubmit = () => {
    if (currentFeedbackValue) {
      onSendPositiveFeedback(feedbackDetails, emailMeCheckboxValue);
    } else {
      onSendNegativeFeedback(reasonValues, feedbackDetails, emailMeCheckboxValue);
    }
    close();
  };

  const reasonList = [
    { code: FEEDBACK_REASON_BROKEN, name: formatMessage(messages.feedbackDetailsModalCheckboxBroken) },
    { code: FEEDBACK_REASON_HARMFUL, name: formatMessage(messages.feedbackDetailsModalCheckboxHarmful) },
    { code: FEEDBACK_REASON_NOT_ACCURATE, name: formatMessage(messages.feedbackDetailsModalCheckboxNotAccurate) },
    { code: FEEDBACK_REASON_OTHER, name: formatMessage(messages.feedbackDetailsModalCheckboxElse) },
  ];

  const handleReasonChange = e => {
    if (e.target.checked) {
      add(e.target.value);
    } else {
      remove(e.target.value);
    }
  };

  const handleDetailsChange = (event) => {
    setFeedbackDetails(event.target.value);
  };

  const handleEmailMeChange = (e) => {
    setEmailMeCheckboxValue(e.target.checked);
  };

  return (
    <StandardModal
      title={formatMessage(messages.feedbackDetailsModalTitle)}
      isOpen={isOpen}
      onClose={close}
      isOverflowVisible={false}
      footerNode={(
        <ActionRow>
          <Button onClick={onSubmit} data-testid="feedback-details-modal-submit-button">
            {formatMessage(messages.feedbackDetailsModalSubmitText)}
          </Button>
        </ActionRow>
      )}
    >
      <div dir={dir}>
        {
          !currentFeedbackValue && (
            <Form.CheckboxSet
              className="mb-3"
              name="reasons"
              onChange={handleReasonChange}
              value={reasonValues}
              data-testid="feedback-details-modal-reasons-checkboxes"
            >
              {reasonList.map((reason) => (
                <Form.Checkbox
                  key={reason.code}
                  value={reason.code}
                  data-testid={`feedback-details-modal-reason-checkbox-${reason.code}`}
                >
                  {reason.name}
                </Form.Checkbox>
              ))}
            </Form.CheckboxSet>
          )
        }
        <Form.Control
          as="textarea"
          onChange={handleDetailsChange}
          placeholder={formatMessage(messages.feedbackDetailsModalPlaceholder)}
          rows="5"
          maxLength="1000"
          data-testid="feedback-details-modal-details"
        />
        <p className="text-right small mr-2">
          {feedbackDetails.length} / 1000
        </p>
      </div>
      <Form.Checkbox
        data-testid="feedback-details-modal-email-me-checkbox"
        checked={emailMeCheckboxValue}
        onChange={handleEmailMeChange}
      >
        {formatMessage(messages.feedbackDetailsModalEmailMeText)}
      </Form.Checkbox>
    </StandardModal>
  );
};

FeedbackDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  currentFeedbackValue: PropTypes.bool.isRequired,
  onSendPositiveFeedback: PropTypes.func.isRequired,
  onSendNegativeFeedback: PropTypes.func.isRequired,
};

export default FeedbackDetailsModal;
